import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { FaUpload, FaDownload, FaFolder, FaEdit, FaTrash, FaMusic, FaCompactDisc, FaTimes, FaSave, FaSearch, FaShare, FaLayerGroup, FaCheck } from 'react-icons/fa';
import '../styles/AdminPage.css';

function AdminPage() {
  const [activeTab, setActiveTab] = useState('import');
  const [albumData, setAlbumData] = useState({
    albumTitle: '',
    category: 'film_soundtrack',
    artist: 'Disney',
    coverUrl: ''
  });
  
  const [coverFile, setCoverFile] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [detectedDiscs, setDetectedDiscs] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [albums, setAlbums] = useState([]);
  const [filteredAlbums, setFilteredAlbums] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editModalTab, setEditModalTab] = useState('info');
  const [editingTracks, setEditingTracks] = useState([]);
  const [editCoverFile, setEditCoverFile] = useState(null);

  // Filtres et recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('title');

  // Partages
  const [shares, setShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);

  // Collections
  const [importMode, setImportMode] = useState('album'); // 'album' ou 'collection'
  const [collectionData, setCollectionData] = useState({ name: '', category: 'film_soundtrack', artist: 'Disney', description: '' });
  const [detectedAlbums, setDetectedAlbums] = useState([]);
  const [collectionImportProgress, setCollectionImportProgress] = useState({ current: 0, total: 0, albumName: '' });
  const [collections, setCollections] = useState([]);
  const [showCollections, setShowCollections] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);

  const getCoverUrl = (coverUrl) => {
    if (!coverUrl) return null;
    if (coverUrl.startsWith('http')) return coverUrl;
    const cleanPath = coverUrl.startsWith('/') ? coverUrl : `/${coverUrl}`;
    return `https://disney-music.gpworld.fr${cleanPath}`;
  };

  useEffect(() => {
    if (activeTab === 'library') {
      loadAlbums();
      loadCollections();
    }
    if (activeTab === 'shares') {
      loadShares();
    }
  }, [activeTab]);

  useEffect(() => {
    filterAndSortAlbums();
  }, [albums, searchQuery, categoryFilter, sortBy]);

  const filterAndSortAlbums = () => {
    let filtered = [...albums];

    // Filtre par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(album =>
        album.title.toLowerCase().includes(query) ||
        album.artist.toLowerCase().includes(query)
      );
    }

    // Filtre par catégorie
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(album => album.category === categoryFilter);
    }

    // Tri
    filtered.sort((a, b) => {
      if (sortBy === 'title') {
        return a.title.localeCompare(b.title);
      } else if (sortBy === 'artist') {
        return a.artist.localeCompare(b.artist);
      } else if (sortBy === 'date') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });

    setFilteredAlbums(filtered);
  };

  const detectDiscNumbers = (files) => {
    const discMap = {};
    
    files.forEach(file => {
      let discNumber = 1;
      
      if (file.webkitRelativePath) {
        const path = file.webkitRelativePath;
        const folderPatterns = [
          /\/CD[\s-_]*(\d+)\//i,
          /\/Disc[\s-_]*(\d+)\//i,
          /\/D[\s-_]*(\d+)\//i,
        ];
        
        for (const pattern of folderPatterns) {
          const match = path.match(pattern);
          if (match) {
            discNumber = parseInt(match[1]);
            break;
          }
        }
      }
      
      if (discNumber === 1) {
        const filename = file.name;
        const filenamePatterns = [
          /CD[\s-_]*(\d+)/i,
          /Disc[\s-_]*(\d+)/i,
          /D[\s-_]*(\d+)/i,
        ];
        
        for (const pattern of filenamePatterns) {
          const match = filename.match(pattern);
          if (match) {
            discNumber = parseInt(match[1]);
            break;
          }
        }
      }
      
      if (!discMap[discNumber]) {
        discMap[discNumber] = [];
      }
      discMap[discNumber].push(file);
    });
    
    const discs = Object.keys(discMap)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(discNum => ({
        number: parseInt(discNum),
        files: discMap[discNum].sort((a, b) => a.name.localeCompare(b.name))
      }));
    
    return discs;
  };

  const extractTitleFromFolder = (files) => {
    if (files.length === 0 || !files[0].webkitRelativePath) return '';
    const path = files[0].webkitRelativePath;
    const parts = path.split('/');
    return parts[0];
  };

  const loadAlbums = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/albums', {
        params: { limit: 1000 }
      });
      setAlbums(response.data.albums || []);
    } catch (error) {
      console.error('Erreur chargement albums:', error);
      setMessage('❌ Erreur chargement albums');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps: getAudioRootProps, getInputProps: getAudioInputProps } = useDropzone({
    accept: {
      'audio/*': ['.mp3', '.flac', '.wma', '.wav', '.m4a', '.ogg', '.aac']
    },
    onDrop: (files) => {
      setAudioFiles(files);
      const discs = detectDiscNumbers(files);
      setDetectedDiscs(discs);
      const discCount = discs.length;
      setMessage(`✅ ${files.length} fichiers ${discCount > 1 ? `(${discCount} CD)` : ''}`);
      setTimeout(() => setMessage(''), 3000);
      
      if (!albumData.albumTitle) {
        const title = extractTitleFromFolder(files);
        if (title && title.length >= 2) {
          setAlbumData(prev => ({ ...prev, albumTitle: title }));
        }
      }
    }
  });

  const { getRootProps: getCoverRootProps, getInputProps: getCoverInputProps } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    },
    maxFiles: 1,
    onDrop: (files) => {
      if (files.length > 0) {
        setCoverFile(files[0]);
        setMessage('✅ Pochette ajoutée');
        setTimeout(() => setMessage(''), 3000);
      }
    }
  });

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const audioFilesFiltered = files.filter(f => 
      /\.(mp3|flac|wma|wav|m4a|ogg|aac)$/i.test(f.name)
    );
    
    if (audioFilesFiltered.length === 0) {
      setMessage('❌ Aucun fichier audio trouvé');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    setAudioFiles(audioFilesFiltered);
    const discs = detectDiscNumbers(audioFilesFiltered);
    setDetectedDiscs(discs);
    
    if (!albumData.albumTitle) {
      const title = extractTitleFromFolder(audioFilesFiltered);
      if (title && title.length >= 2) {
        setAlbumData(prev => ({ ...prev, albumTitle: title }));
        const discCount = discs.length;
        setMessage(`✅ "${title}" - ${audioFilesFiltered.length} fichiers ${discCount > 1 ? `(${discCount} CD)` : ''}`);
        setTimeout(() => setMessage(''), 5000);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAlbumData(prev => ({ ...prev, [name]: value }));
  };

  const handleImport = async () => {
    if (!albumData.albumTitle) {
      setMessage('❌ Titre requis');
      return;
    }

    if (audioFiles.length === 0) {
      setMessage('❌ Ajoutez des fichiers audio');
      return;
    }

    setIsUploading(true);
    setMessage('⏳ Import...');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('title', albumData.albumTitle);
    formData.append('category', albumData.category);
    formData.append('artist', albumData.artist);
    formData.append('coverUrl', albumData.coverUrl);

    audioFiles.forEach(file => {
      let discNumber = 1;
      for (const disc of detectedDiscs) {
        if (disc.files.includes(file)) {
          discNumber = disc.number;
          break;
        }
      }
      
      formData.append('audio', file);
      formData.append('disc_numbers', discNumber);
    });

    if (coverFile) {
      formData.append('cover', coverFile);
    }

    try {
      await axios.post('/api/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setMessage('✅ Import réussi !');
      
      setAlbumData({
        albumTitle: '',
        category: 'film_soundtrack',
        artist: 'Disney',
        coverUrl: ''
      });
      setCoverFile(null);
      setAudioFiles([]);
      setDetectedDiscs([]);
      setUploadProgress(0);
      
      setTimeout(() => setMessage(''), 3000);

    } catch (error) {
      console.error('Erreur import:', error);
      setMessage('❌ Erreur : ' + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditAlbum = async (album) => {
    try {
      const response = await axios.get(`/api/albums/${album.id}`);
      setEditingAlbum({
        id: album.id,
        title: album.title,
        artist: album.artist,
        category: album.category,
        coverUrl: album.cover_url || '',
        currentCover: album.cover_url,
        collection_id: album.collection_id || ''
      });
      setEditingTracks(response.data.tracks || []);
      setEditModalTab('info');
      setShowEditModal(true);
      loadCollections();
    } catch (error) {
      console.error('Erreur chargement album:', error);
      setMessage('❌ Erreur chargement album');
    }
  };

  const handleSaveEdit = async () => {
    try {
      let coverUrl = editingAlbum.coverUrl;
      if (editCoverFile) {
        const formData = new FormData();
        formData.append('cover', editCoverFile);
        const coverRes = await axios.post(`/api/admin/albums/${editingAlbum.id}/cover`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        coverUrl = coverRes.data.cover_url;
      }
      await axios.put(`/api/admin/albums/${editingAlbum.id}`, {
        title: editingAlbum.title,
        artist: editingAlbum.artist,
        category: editingAlbum.category,
        coverUrl: coverUrl,
        collection_id: editingAlbum.collection_id || null
      });

      for (const track of editingTracks) {
        if (track.modified && !track.deleted) {
          await axios.put(`/api/admin/tracks/${track.id}`, {
            title: track.title,
            artist: track.artist,
            track_number: track.track_number,
            disc_number: track.disc_number
          });
        }
        if (track.deleted) {
          await axios.delete(`/api/admin/tracks/${track.id}`);
        }
      }

      setMessage('✅ Album modifié !');
      setTimeout(() => setMessage(''), 3000);
      setShowEditModal(false);
      setEditingAlbum(null);
      setEditingTracks([]);
      setEditCoverFile(null);
      loadAlbums();
    } catch (error) {
      console.error('Erreur modification:', error);
      setMessage('❌ Erreur modification');
    }
  };

  const handleTrackChange = (trackId, field, value) => {
    setEditingTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId
          ? { ...track, [field]: value, modified: true }
          : track
      )
    );
  };

  const handleDeleteTrack = (trackId) => {
    if (!window.confirm('Supprimer cette piste ?')) return;
    
    setEditingTracks(prevTracks =>
      prevTracks.map(track =>
        track.id === trackId
          ? { ...track, deleted: true }
          : track
      )
    );
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Supprimer cet album ?')) return;

    try {
      await axios.delete(`/api/admin/albums/${albumId}`);
      setMessage('✅ Album supprimé');
      setTimeout(() => setMessage(''), 3000);
      loadAlbums();
    } catch (error) {
      console.error('Erreur suppression:', error);
      setMessage('❌ Erreur suppression');
    }
  };

  const handleBackup = async () => {
    try {
      setMessage('⏳ Création backup...');
      const response = await axios.post('/api/admin/backup', {}, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `disney-music-backup-${Date.now()}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setMessage('✅ Backup téléchargé !');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage('❌ Erreur backup');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'film_soundtrack': 'BO de Film',
      'disney_park': 'Disney Park',
      'public_album': 'Grand Public'
    };
    return labels[category] || category;
  };

  const loadShares = async () => {
    try {
      setLoadingShares(true);
      const response = await axios.get('/api/share/admin/list');
      setShares(response.data || []);
    } catch (error) {
      console.error('Erreur chargement partages:', error);
      setMessage('❌ Erreur chargement partages');
    } finally {
      setLoadingShares(false);
    }
  };

  const deleteShare = async (shareId) => {
    if (!window.confirm('Supprimer ce lien de partage ?')) return;
    try {
      await axios.delete(`/api/share/admin/${shareId}`);
      setShares(shares.filter(s => s.id !== shareId));
      setMessage('✅ Lien supprimé');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Erreur suppression partage:', error);
      setMessage('❌ Erreur suppression');
    }
  };

  const formatShareDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getShareStatusBadge = (status) => {
    const map = {
      active: { label: '🟢 Actif', className: 'badge-active' },
      expired: { label: '🔴 Expiré', className: 'badge-expired' },
      maxed: { label: '🟠 Max atteint', className: 'badge-maxed' }
    };
    return map[status] || { label: status, className: '' };
  };

  // ===== COLLECTION FUNCTIONS =====
  const loadCollections = async () => {
    try {
      const response = await axios.get('/api/admin/collections');
      setCollections(response.data);
    } catch (error) {
      console.error('Erreur chargement collections:', error);
    }
  };

  const analyzeCollectionFolder = (fileList) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;

    const rootName = files[0]?.webkitRelativePath?.split('/')[0] || 'Collection';
    const albumMap = {};
    const audioExts = ['mp3', 'flac', 'wav', 'wma', 'm4a', 'ogg', 'aac'];
    const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'gif'];

    files.forEach(file => {
      const parts = file.webkitRelativePath.split('/');
      
      if (parts.length < 3) {
        // Fichier à la racine - on ignore les images, on traite les audio comme album unique
        return;
      }

      const albumName = parts[1];
      if (!albumMap[albumName]) {
        albumMap[albumName] = { name: albumName, audio: [], cover: null, coverPreview: null };
      }

      const ext = file.name.split('.').pop().toLowerCase();
      const nameLower = file.name.toLowerCase();

      if (audioExts.includes(ext)) {
        albumMap[albumName].audio.push(file);
      } else if (imageExts.includes(ext)) {
        // Priorité : folder.jpg > cover.jpg > front.jpg > n'importe quelle image
        const priority = nameLower.includes('folder') ? 3 :
                        nameLower.includes('cover') ? 2 :
                        nameLower.includes('front') ? 1 : 0;
        const currentPriority = albumMap[albumName]._coverPriority || -1;
        if (priority > currentPriority || !albumMap[albumName].cover) {
          albumMap[albumName].cover = file;
          albumMap[albumName].coverPreview = URL.createObjectURL(file);
          albumMap[albumName]._coverPriority = priority;
        }
      }
    });

    const albums = Object.values(albumMap)
      .filter(a => a.audio.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));

    setCollectionData(prev => ({ ...prev, name: rootName }));
    setDetectedAlbums(albums);
    setMessage(`✅ ${albums.length} album(s) détecté(s) dans "${rootName}"`);
  };

  const handleCollectionFolderSelect = (e) => {
    analyzeCollectionFolder(e.target.files);
  };

  const handleCollectionImport = async () => {
    if (!collectionData.name.trim() || detectedAlbums.length === 0) {
      setMessage('❌ Nom de collection et au moins un album requis');
      return;
    }

    setIsUploading(true);
    setCollectionImportProgress({ current: 0, total: detectedAlbums.length, albumName: '' });

    try {
      // 1. Créer la collection
      const collRes = await axios.post('/api/admin/collections', {
        name: collectionData.name.trim(),
        category: collectionData.category,
        description: collectionData.description.trim() || null
      });
      const collectionId = collRes.data.id;
      console.log('✅ Collection créée, ID:', collectionId);

      // 2. Importer chaque album
      for (let i = 0; i < detectedAlbums.length; i++) {
        const album = detectedAlbums[i];
        setCollectionImportProgress({ current: i + 1, total: detectedAlbums.length, albumName: album.name });

        const formData = new FormData();
        formData.append('title', album.name);
        formData.append('category', collectionData.category);
        formData.append('artist', collectionData.artist);
        formData.append('collection_id', collectionId);

        if (album.cover) {
          formData.append('cover', album.cover);
        }

        // Trier les fichiers audio par nom pour garder l'ordre
        const sortedAudio = [...album.audio].sort((a, b) => a.name.localeCompare(b.name));
        sortedAudio.forEach(file => {
          formData.append('audio', file);
          formData.append('disc_numbers', '1');
        });

        await axios.post('/api/admin/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / e.total) * 100);
            setUploadProgress(pct);
          }
        });

        console.log(`✅ Album ${i + 1}/${detectedAlbums.length}: "${album.name}"`);
      }

      setMessage(`✅ Collection "${collectionData.name}" importée avec ${detectedAlbums.length} albums !`);
      setDetectedAlbums([]);
      setCollectionData({ name: '', category: 'film_soundtrack', artist: 'Disney', description: '' });
      setUploadProgress(0);
    } catch (error) {
      console.error('❌ Erreur import collection:', error);
      setMessage(`❌ Erreur : ${error.response?.data?.error || error.message}`);
    } finally {
      setIsUploading(false);
      setCollectionImportProgress({ current: 0, total: 0, albumName: '' });
    }
  };

  const deleteCollection = async (collectionId) => {
    if (!window.confirm('Supprimer cette collection ? Les albums resteront mais ne seront plus groupés.')) return;
    try {
      await axios.delete(`/api/admin/collections/${collectionId}`);
      setCollections(collections.filter(c => c.id !== collectionId));
      setMessage('✅ Collection supprimée');
    } catch (error) {
      console.error('Erreur suppression collection:', error);
      setMessage('❌ Erreur suppression collection');
    }
  };

  const updateCollection = async (collectionId, data) => {
    try {
      await axios.put(`/api/admin/collections/${collectionId}`, data);
      loadCollections();
      setEditingCollection(null);
      setMessage('✅ Collection mise à jour');
    } catch (error) {
      console.error('Erreur modification collection:', error);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <h1>🎵 Administration Disney-Music</h1>

        <div className="admin-tabs">
          <button 
            className={`tab-btn ${activeTab === 'import' ? 'active' : ''}`}
            onClick={() => setActiveTab('import')}
          >
            <FaUpload /> Importer
          </button>
          <button 
            className={`tab-btn ${activeTab === 'library' ? 'active' : ''}`}
            onClick={() => setActiveTab('library')}
          >
            <FaMusic /> Bibliothèque
          </button>
          <button 
            className={`tab-btn ${activeTab === 'backup' ? 'active' : ''}`}
            onClick={() => setActiveTab('backup')}
          >
            <FaDownload /> Sauvegarde
          </button>
          <button 
            className={`tab-btn ${activeTab === 'shares' ? 'active' : ''}`}
            onClick={() => setActiveTab('shares')}
          >
            <FaShare /> Partages
          </button>
        </div>

        {message && (
          <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        {activeTab === 'import' && (
          <div className="admin-section">
            {/* Toggle Album / Collection */}
            <div className="import-mode-toggle">
              <button 
                className={`toggle-btn ${importMode === 'album' ? 'active' : ''}`}
                onClick={() => { setImportMode('album'); setDetectedAlbums([]); }}
              >
                <FaCompactDisc /> Album simple
              </button>
              <button 
                className={`toggle-btn ${importMode === 'collection' ? 'active' : ''}`}
                onClick={() => { setImportMode('collection'); setAudioFiles([]); }}
              >
                <FaLayerGroup /> Collection (multi-albums)
              </button>
            </div>

            {importMode === 'collection' ? (
              /* ===== MODE COLLECTION ===== */
              <div className="collection-import">
                <div className="form-group">
                  <label>Nom de la collection *</label>
                  <input
                    type="text"
                    value={collectionData.name}
                    onChange={(e) => setCollectionData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Star Wars, The Mandalorian..."
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Catégorie *</label>
                  <select
                    value={collectionData.category}
                    onChange={(e) => setCollectionData(prev => ({ ...prev, category: e.target.value }))}
                    className="form-input"
                  >
                    <option value="film_soundtrack">BO de Film</option>
                    <option value="disney_park">Album Disney Park</option>
                    <option value="public_album">Album Grand Public</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Artiste</label>
                  <input
                    type="text"
                    value={collectionData.artist}
                    onChange={(e) => setCollectionData(prev => ({ ...prev, artist: e.target.value }))}
                    placeholder="John Williams, Ludwig Göransson..."
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Description (optionnel)</label>
                  <input
                    type="text"
                    value={collectionData.description}
                    onChange={(e) => setCollectionData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Tous les albums de la saga..."
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Dossier contenant les sous-dossiers (= albums) *</label>
                  <label className="file-input-button folder-button">
                    <FaFolder size={20} style={{ marginRight: '8px' }} />
                    Sélectionner le dossier de la collection
                    <input
                      type="file"
                      webkitdirectory=""
                      directory=""
                      multiple
                      onChange={handleCollectionFolderSelect}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <p className="small-text" style={{ marginTop: '8px', color: '#94a3b8' }}>
                    Structure attendue : Dossier Principal / Sous-dossier 1 (album) / fichiers audio + cover
                  </p>
                </div>

                {/* Preview des albums détectés */}
                {detectedAlbums.length > 0 && (
                  <div className="collection-preview">
                    <h3><FaLayerGroup /> {detectedAlbums.length} album(s) détecté(s)</h3>
                    <div className="detected-albums-list">
                      {detectedAlbums.map((album, idx) => (
                        <div key={idx} className="detected-album-item">
                          <div className="detected-album-cover">
                            {album.coverPreview ? (
                              <img src={album.coverPreview} alt={album.name} />
                            ) : (
                              <div className="no-cover-detected">🎵</div>
                            )}
                          </div>
                          <div className="detected-album-info">
                            <input
                              type="text"
                              value={album.name}
                              onChange={(e) => {
                                const updated = [...detectedAlbums];
                                updated[idx].name = e.target.value;
                                setDetectedAlbums(updated);
                              }}
                              className="detected-album-name-input"
                            />
                            <span className="detected-album-meta">
                              {album.audio.length} piste(s)
                              {album.cover ? ' • 🖼️ Pochette détectée' : ' • ⚠️ Pas de pochette'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress collection import */}
                {isUploading && collectionImportProgress.total > 0 && (
                  <div className="collection-progress">
                    <p>📀 Import album {collectionImportProgress.current}/{collectionImportProgress.total} : <strong>{collectionImportProgress.albumName}</strong></p>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${(collectionImportProgress.current / collectionImportProgress.total) * 100}%` }}>
                        {collectionImportProgress.current}/{collectionImportProgress.total}
                      </div>
                    </div>
                    {uploadProgress > 0 && (
                      <div className="progress-bar" style={{ marginTop: '6px' }}>
                        <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                          Upload : {uploadProgress}%
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleCollectionImport}
                  disabled={isUploading || detectedAlbums.length === 0 || !collectionData.name.trim()}
                  className="btn btn-primary btn-large"
                >
                  {isUploading ? `⏳ Import ${collectionImportProgress.current}/${collectionImportProgress.total}...` : `📦 Importer ${detectedAlbums.length} album(s)`}
                </button>
              </div>
            ) : (
            /* ===== MODE ALBUM SIMPLE ===== */
            <>
            <div className="form-group">
              <label htmlFor="albumTitle">Titre de l'album *</label>
              <input
                type="text"
                id="albumTitle"
                name="albumTitle"
                value={albumData.albumTitle}
                onChange={handleInputChange}
                placeholder="Le Roi Lion"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Catégorie *</label>
              <select
                id="category"
                name="category"
                value={albumData.category}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="film_soundtrack">BO de Film</option>
                <option value="disney_park">Album Disney Park</option>
                <option value="public_album">Album Grand Public</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="artist">Artiste</label>
              <input
                type="text"
                id="artist"
                name="artist"
                value={albumData.artist}
                onChange={handleInputChange}
                placeholder="Hans Zimmer"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Pochette</label>
              {coverFile ? (
                <div className="file-preview">
                  <img 
                    src={URL.createObjectURL(coverFile)} 
                    alt="Aperçu" 
                    style={{ maxWidth: '200px', borderRadius: '8px' }}
                  />
                  <button 
                    type="button"
                    onClick={() => setCoverFile(null)}
                    className="btn btn-sm btn-danger"
                    style={{ marginTop: '10px' }}
                  >
                    Supprimer
                  </button>
                </div>
              ) : (
                <div {...getCoverRootProps()} className="dropzone">
                  <input {...getCoverInputProps()} />
                  <FaUpload size={40} />
                  <p>Glissez une image</p>
                  <p className="small-text">PNG, JPG, GIF</p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="coverUrl">OU URL pochette</label>
              <input
                type="url"
                id="coverUrl"
                name="coverUrl"
                value={albumData.coverUrl}
                onChange={handleInputChange}
                placeholder="https://..."
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Fichiers audio *</label>
              
              <label className="file-input-button folder-button">
                <FaFolder size={20} style={{ marginRight: '8px' }} />
                Sélectionner un dossier
                <input
                  type="file"
                  webkitdirectory=""
                  directory=""
                  multiple
                  onChange={handleFolderSelect}
                  style={{ display: 'none' }}
                />
              </label>

              <div {...getAudioRootProps()} className="dropzone" style={{ marginTop: '10px' }}>
                <input {...getAudioInputProps()} />
                <FaUpload size={40} />
                <p>Ou glissez des fichiers</p>
                <p className="small-text">MP3, FLAC, WMA, WAV</p>
              </div>

              {audioFiles.length > 0 && (
                <div className="files-list">
                  {detectedDiscs.length > 1 ? (
                    <>
                      <p><strong>{audioFiles.length} fichiers - {detectedDiscs.length} CD</strong></p>
                      {detectedDiscs.map(disc => (
                        <div key={disc.number} className="disc-group">
                          <p className="disc-title">
                            <FaCompactDisc /> CD {disc.number} ({disc.files.length} pistes)
                          </p>
                          <ul>
                            {disc.files.slice(0, 5).map((file, index) => (
                              <li key={index}>🎵 {file.name}</li>
                            ))}
                            {disc.files.length > 5 && (
                              <li style={{ color: '#94a3b8' }}>... et {disc.files.length - 5} autres</li>
                            )}
                          </ul>
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <p><strong>{audioFiles.length} fichiers</strong></p>
                      <ul>
                        {audioFiles.slice(0, 10).map((file, index) => (
                          <li key={index}>🎵 {file.name}</li>
                        ))}
                        {audioFiles.length > 10 && (
                          <li style={{ color: '#94a3b8' }}>... et {audioFiles.length - 10} autres</li>
                        )}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>

            {isUploading && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                    {uploadProgress}%
                  </div>
                </div>
                <p className="progress-text">Upload : {uploadProgress}%</p>
              </div>
            )}

            <button 
              onClick={handleImport} 
              disabled={isUploading}
              className="btn btn-primary btn-large"
            >
              {isUploading ? '⏳ Import...' : '➕ Importer'}
            </button>
            </>
            )}
          </div>
        )}

        {activeTab === 'library' && (
          <div className="admin-section">
            <div className="library-header">
              <h2>📚 Bibliothèque ({filteredAlbums.length} / {albums.length} albums)</h2>
              
              {/* Barre de recherche */}
              <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher un album ou artiste..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="filter-group">
                  <label>Catégorie :</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">Toutes</option>
                    <option value="film_soundtrack">BO de Film</option>
                    <option value="disney_park">Disney Park</option>
                    <option value="public_album">Grand Public</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Trier par :</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="filter-select"
                  >
                    <option value="title">Titre</option>
                    <option value="artist">Artiste</option>
                    <option value="date">Date d'ajout</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Collections */}
            {collections.length > 0 && (
              <div className="collections-admin-section">
                <h3 className="collapsible-header" onClick={() => setShowCollections(!showCollections)} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:"8px"}}><span style={{transition:"transform 0.3s",transform:showCollections?"rotate(90deg)":"rotate(0)",display:"inline-block"}}>▶</span><FaLayerGroup /> Collections ({collections.length})</h3>
                {showCollections && <div className="collections-admin-list">
                  {collections.map(col => (
                    <div key={col.id} className="collection-admin-item">
                      <div className="collection-admin-info">
                        <strong>{col.name}</strong>
                        <span className="collection-admin-meta">{col.album_count} album(s) • {col.category}</span>
                      </div>
                      <div className="collection-admin-actions">
                        <button className="btn btn-sm" onClick={() => {
                          setEditingCollection(col);
                        }}><FaEdit /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteCollection(col.id)}><FaTrash /></button>
                      </div>
                    </div>
                  ))}
                </div>}
              </div>
            )}
            
            {loading ? (
              <p>Chargement...</p>
            ) : filteredAlbums.length === 0 ? (
              <p className="no-results">
                {searchQuery || categoryFilter !== 'all' 
                  ? 'Aucun album ne correspond à vos critères' 
                  : 'Aucun album'
                }
              </p>
            ) : (
              <div className="albums-grid">
                {filteredAlbums.map(album => (
                  <div key={album.id} className="album-card">
                    <div className="album-cover">
                      {album.cover_url ? (
                        <img src={getCoverUrl(album.cover_url)} alt={album.title} />
                      ) : (
                        <div className="album-cover-placeholder">
                          <span>🎵</span>
                        </div>
                      )}
                    </div>
                    <div className="album-info">
                      <div className="album-title" title={album.title}>{album.title}</div>
                      <div className="album-meta">
                        <span title={album.artist}>{album.artist}</span>
                      </div>
                      <div className="album-category-badge">{getCategoryLabel(album.category)}</div>
                      {album.collection_id && (
                        <div className="album-collection-badge">
                          <FaLayerGroup /> {collections.find(c => c.id === album.collection_id)?.name || 'Collection'}
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '10px' }}>
                        <button 
                          className="btn btn-sm btn-primary" 
                          style={{ flex: 1 }}
                          onClick={() => handleEditAlbum(album)}
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => handleDeleteAlbum(album.id)}
                          style={{ flex: 1 }}
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'backup' && (
          <div className="admin-section">
            <h2>💾 Sauvegarde</h2>
            <p>Créer un backup complet.</p>
            <button onClick={handleBackup} className="btn btn-secondary btn-large">
              <FaDownload style={{ marginRight: '8px' }} />
              Créer un backup
            </button>
          </div>
        )}

        {activeTab === 'shares' && (
          <div className="admin-section">
            <h2>🔗 Liens de partage ({shares.length})</h2>
            <p>Gérez les liens de partage éphémères créés par les utilisateurs.</p>
            
            {loadingShares ? (
              <p>Chargement...</p>
            ) : shares.length === 0 ? (
              <p className="no-results">Aucun lien de partage</p>
            ) : (
              <div className="shares-table-container">
                <table className="shares-table">
                  <thead>
                    <tr>
                      <th>Statut</th>
                      <th>Contenu partagé</th>
                      <th>Lien</th>
                      <th>Créé par</th>
                      <th>Créé le</th>
                      <th>Expire le</th>
                      <th>Lectures</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shares.map(share => {
                      const badge = getShareStatusBadge(share.status);
                      return (
                        <tr key={share.id} className={`share-row share-row-${share.status}`}>
                          <td>
                            <span className={`share-badge ${badge.className}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="share-content-cell">
                            {share.shared_content}
                          </td>
                          <td className="share-link-cell">
                            <div className="share-link-box">
                              <a 
                                href={`https://disney-music.gpworld.fr/share/${share.token}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="share-link-text"
                                title={`https://disney-music.gpworld.fr/share/${share.token}`}
                              >
                                ...{share.token?.substring(0, 12)}
                              </a>
                              <button
                                className="share-copy-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = `https://disney-music.gpworld.fr/share/${share.token}`;
                                  navigator.clipboard.writeText(url).catch(() => {
                                    const ta = document.createElement('textarea');
                                    ta.value = url;
                                    document.body.appendChild(ta);
                                    ta.select();
                                    document.execCommand('copy');
                                    document.body.removeChild(ta);
                                  });
                                  setMessage('✅ Lien copié !');
                                  setTimeout(() => setMessage(''), 2000);
                                }}
                                title="Copier le lien"
                              >
                                📋
                              </button>
                            </div>
                          </td>
                          <td>{share.created_by_username || 'Inconnu'}</td>
                          <td>{formatShareDate(share.created_at)}</td>
                          <td>{formatShareDate(share.expires_at)}</td>
                          <td>
                            {share.play_count}{share.max_plays ? ` / ${share.max_plays}` : ' / ∞'}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => deleteShare(share.id)}
                              title="Supprimer"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {showEditModal && editingAlbum && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>✏️ Modifier l'album</h2>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-sm"
                  style={{ padding: '8px' }}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="modal-tabs">
                <button
                  className={`modal-tab-btn ${editModalTab === 'info' ? 'active' : ''}`}
                  onClick={() => setEditModalTab('info')}
                >
                  📋 Informations
                </button>
                <button
                  className={`modal-tab-btn ${editModalTab === 'tracks' ? 'active' : ''}`}
                  onClick={() => setEditModalTab('tracks')}
                >
                  🎵 Pistes ({editingTracks.filter(t => !t.deleted).length})
                </button>
              </div>

              {editModalTab === 'info' && (
                <>
                  <div className="form-group">
                    <label>Titre *</label>
                    <input
                      type="text"
                      value={editingAlbum.title}
                      onChange={(e) => setEditingAlbum({...editingAlbum, title: e.target.value})}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Artiste</label>
                    <input
                      type="text"
                      value={editingAlbum.artist}
                      onChange={(e) => setEditingAlbum({...editingAlbum, artist: e.target.value})}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Catégorie</label>
                    <select
                      value={editingAlbum.category}
                      onChange={(e) => setEditingAlbum({...editingAlbum, category: e.target.value})}
                      className="form-input"
                    >
                      <option value="film_soundtrack">BO de Film</option>
                      <option value="disney_park">Album Disney Park</option>
                      <option value="public_album">Album Grand Public</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label><FaLayerGroup style={{ marginRight: '6px' }} />Collection</label>
                    <select
                      value={editingAlbum.collection_id || ''}
                      onChange={(e) => setEditingAlbum({...editingAlbum, collection_id: e.target.value || null})}
                      className="form-input"
                    >
                      <option value="">— Aucune collection —</option>
                      {collections.map(col => (
                        <option key={col.id} value={col.id}>{col.name} ({col.album_count} albums)</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Pochette</label>
                    {(editCoverFile || editingAlbum.currentCover) && (
                      <div style={{ marginBottom: '10px', position: 'relative', display: 'inline-block' }}>
                        <img 
                          src={editCoverFile ? URL.createObjectURL(editCoverFile) : getCoverUrl(editingAlbum.currentCover)}
                          alt="Pochette" 
                          style={{ maxWidth: '150px', borderRadius: '8px' }}
                        />
                        {editCoverFile && (
                          <button 
                            onClick={() => setEditCoverFile(null)}
                            style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '12px' }}
                          >✕</button>
                        )}
                      </div>
                    )}
                    <div 
                      className="edit-cover-dropzone"
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
                      onDragLeave={(e) => { e.currentTarget.classList.remove('dragover'); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.currentTarget.classList.remove('dragover');
                        const file = e.dataTransfer.files[0];
                        if (file && file.type.startsWith('image/')) {
                          setEditCoverFile(file);
                          setEditingAlbum({...editingAlbum, coverUrl: ''});
                        }
                      }}
                      onClick={() => document.getElementById('editCoverInput').click()}
                    >
                      <input 
                        id="editCoverInput"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setEditCoverFile(file);
                            setEditingAlbum({...editingAlbum, coverUrl: ''});
                          }
                        }}
                      />
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
                        {editCoverFile ? '✅ Nouvelle pochette' : '📁 Glissez une image ou cliquez'}
                      </p>
                    </div>
                    <input
                      type="url"
                      value={editingAlbum.coverUrl}
                      onChange={(e) => { setEditingAlbum({...editingAlbum, coverUrl: e.target.value}); setEditCoverFile(null); }}
                      className="form-input"
                      placeholder="Ou collez une URL..."
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </>
              )}

              {editModalTab === 'tracks' && (
                <div className="tracks-edit-container">
                  {editingTracks.filter(t => !t.deleted).map((track, index) => {
                    const visibleTracks = editingTracks.filter(t => !t.deleted);
                    const prevTrack = visibleTracks[index - 1];
                    const showDiscHeader = !prevTrack || prevTrack.disc_number !== track.disc_number;

                    return (
                      <React.Fragment key={track.id}>
                        {showDiscHeader && visibleTracks.some(t => t.disc_number > 1) && (
                          <div className="disc-header-edit">CD {track.disc_number}</div>
                        )}
                        
                        <div className="track-edit-row">
                          <input
                            type="number"
                            value={track.track_number}
                            onChange={(e) => handleTrackChange(track.id, 'track_number', parseInt(e.target.value))}
                            className="track-input track-num-input"
                            title="N° piste"
                          />
                          <input
                            type="number"
                            value={track.disc_number}
                            onChange={(e) => handleTrackChange(track.id, 'disc_number', parseInt(e.target.value))}
                            className="track-input track-disc-input"
                            title="N° CD"
                          />
                          <input
                            type="text"
                            value={track.title}
                            onChange={(e) => handleTrackChange(track.id, 'title', e.target.value)}
                            className="track-input track-title-input"
                            placeholder="Titre"
                          />
                          <input
                            type="text"
                            value={track.artist}
                            onChange={(e) => handleTrackChange(track.id, 'artist', e.target.value)}
                            className="track-input track-artist-input"
                            placeholder="Artiste"
                          />
                          <button
                            onClick={() => handleDeleteTrack(track.id)}
                            className="btn btn-sm btn-danger track-delete-btn"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button 
                  onClick={handleSaveEdit}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  <FaSave /> Enregistrer
                </button>
                <button 
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal édition collection */}
      {editingCollection && (
        <div className="modal-overlay" onClick={() => setEditingCollection(null)}>
          <div className="edit-collection-modal" onClick={(e) => e.stopPropagation()}>
            <h3><FaLayerGroup /> Modifier la collection</h3>
            <div className="form-group">
              <label>Nom</label>
              <input
                type="text"
                value={editingCollection.name}
                onChange={(e) => setEditingCollection({ ...editingCollection, name: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>Catégorie</label>
              <select
                value={editingCollection.category}
                onChange={(e) => setEditingCollection({ ...editingCollection, category: e.target.value })}
                className="form-input"
              >
                <option value="film_soundtrack">BO de Film</option>
                <option value="disney_park">Disney Park</option>
                <option value="public_album">Grand Public</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                value={editingCollection.description || ''}
                onChange={(e) => setEditingCollection({ ...editingCollection, description: e.target.value })}
                className="form-input"
                placeholder="Description..."
              />
            </div>
            <div className="modal-actions" style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={() => updateCollection(editingCollection.id, editingCollection)}
                className="btn btn-primary"
                style={{ flex: 1 }}
              >
                <FaSave /> Enregistrer
              </button>
              <button
                onClick={() => setEditingCollection(null)}
                className="btn btn-secondary"
                style={{ flex: 1 }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
